import React, { createContext, useState, useEffect, useCallback } from "react";
import { PUBLIC_URL } from "./utils/publicPath";

const DEFAULT_GRID_NODE_STATES = {
  external:  'red',
  corporate: 'amber',
  dmz:       'amber',
  ot:        'green',
  field:     'green',
};

const GRID_NODE_IDS = ['external', 'corporate', 'dmz', 'ot', 'field'];
const GRID_STATE_CYCLE = ['green', 'amber', 'red'];

// Create a context with a default value
const UserProgressContext = createContext({
  audioMuted: true,
  audioSrc: "game-bg-sound",
  // clientIp: null,
  isStartOver: false,
  isUserProgressFetched: false,
  progress: null,
  localProgress: null,
  isLevelEnd: false,
  name: "",
  emailId: "",
  totalScore: 0,
  redeemedScores: new Set(),
  currentTool: null,
  difficulty: null,
  setDifficulty: () => {},
  finishedTasks: new Set(),
  firstAttemptKeys: new Set(),
  addFirstAttemptKey: () => {},
  completedMinigames: new Set(),
  addCompletedMinigame: () => {},
  nextButtonLocked: false,
  setNextButtonLocked: () => {},
  gridNodeStates: DEFAULT_GRID_NODE_STATES,
  setGridNodeStates: () => {},
  flipNode: () => {},
  setIsStartOver: () => {},
  setIsUserProgressFetched: () => {},
  setAudioMuted: () => {},
  // setClientIP: () => {},
  setProgress: () => {},
  setLocalProgress: () => {},
  setIsLevelEnd: () => {},
  setName: () => {},
  setEmailId: () => {},
  setTotalScore: () => {},
  setCurrentTool: () => {},
});

export const UserProgressProvider = ({ children }) => {
  // Initialize state from session storage
  const initialIsStartOver =
    JSON.parse(sessionStorage.getItem("isStartOver")) || false;
  const initialUserProgressFetched =
    JSON.parse(sessionStorage.getItem("isUserProgressFetched")) || false;
  const initialProgress =
    JSON.parse(sessionStorage.getItem("progress")) || null;
  const initialLocalProgress =
    JSON.parse(sessionStorage.getItem("localProgress")) || null;
  const initialIsLevelEnd =
    JSON.parse(sessionStorage.getItem("isLevelEnd")) || false;
  const initialName = sessionStorage.getItem("name") || "";
  const initialEmailId = sessionStorage.getItem("emailId") || "";
  const initialScore = sessionStorage.getItem("totalScore") || 0;
  const initialRedeemedScores = new Set(
    JSON.parse(sessionStorage.getItem("redeemedScores")) || []
  );
  const initialCurrentTool =
    JSON.parse(sessionStorage.getItem("currentTool")) || null;
  const initialFinishedTasks = new Set(
    JSON.parse(sessionStorage.getItem("finishedTasks")) || []
  );
  const initialFirstAttemptKeys = new Set(
    JSON.parse(sessionStorage.getItem("firstAttemptKeys")) || []
  );
  const initialCompletedMinigames = new Set(
    JSON.parse(sessionStorage.getItem("completedMinigames")) || []
  );
  const initialGridNodeStates =
    JSON.parse(sessionStorage.getItem("gridNodeStates")) || DEFAULT_GRID_NODE_STATES;
  const initialDifficulty =
    sessionStorage.getItem("difficulty") || localStorage.getItem("lastDifficulty") || null;

  const [audioMuted, setAudioMuted] = useState(true);
  const [audioSrc, setAudioSrc] = useState("game-bg-sound");
  // const [clientIP, setClientIP] = useState(null);
  const [isStartOver, setIsStartOver] = useState(initialIsStartOver);
  const [isUserProgressFetched, setIsUserProgressFetched] = useState(
    initialUserProgressFetched
  );
  const [progress, setProgress] = useState(initialProgress);
  const [localProgress, setLocalProgress] = useState(initialLocalProgress);
  const [isLevelEnd, setIsLevelEnd] = useState(initialIsLevelEnd);
  const [name, setName] = useState(initialName);
  const [emailId, setEmailId] = useState(initialEmailId);
  const [totalScore, setTotalScore] = useState(initialScore);
  const [redeemedScores, setRedeemedScores] = useState(initialRedeemedScores);
  const [currentTool, setCurrentTool] = useState(initialCurrentTool);
  const [finishedTasks, setFinishedTasks] = useState(initialFinishedTasks);
  const [firstAttemptKeys, setFirstAttemptKeys] = useState(initialFirstAttemptKeys);
  const [completedMinigames, setCompletedMinigames] = useState(initialCompletedMinigames);
  const [nextButtonLocked, setNextButtonLocked] = useState(false);
  const [gridNodeStates, setGridNodeStates] = useState(initialGridNodeStates);
  const [difficulty, setDifficulty] = useState(initialDifficulty);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Function to fetch user progress from backend
  // const fetchUserProgressFromBackend = async () => {
  //   try {
  //     const response = await httpService.get(axios.baseURL, {
  //       params: { userName: name, ip: clientIP },
  //     });

  //     if (response.data && response.data.length > 0) {
  //       const userData = response.data[0];

  //       console.log(
  //         "userData @fetchUserProgressFromBackend",
  //         userData,
  //         userData.progress,
  //         userData.redeemedScores
  //       );
  //       // const { progress, totalScore, redeemedScores, finishedTasks } = userData;
  //       if (progress === null) {
  //         setProgress(userData.progress);
  //         sessionStorage.setItem("progress", JSON.stringify(userData.progress));
  //       }
  //       if (totalScore === 0) {
  //         setTotalScore(userData.totalScore);
  //         sessionStorage.setItem(
  //           "totalScore",
  //           JSON.stringify(userData.totalScore)
  //         );
  //       }
  //       if (redeemedScores.size === 0) {
  //         setRedeemedScores(new Set(userData.redeemedScores));
  //         sessionStorage.setItem(
  //           "redeemedScores",
  //           JSON.stringify(userData.redeemedScores)
  //         );
  //       }
  //       if (finishedTasks.size === 0) {
  //         setFinishedTasks(new Set(userData.finishedTasks));
  //         sessionStorage.setItem(
  //           "finishedTasks",
  //           JSON.stringify(userData.finishedTasks)
  //         );
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error fetching user progress:", error);
  //   }
  // };

  const updateUserProgress = async (key, value) => {
    try {
      sessionStorage.setItem(key, value);
      // if (name && emailId) {
      //   console.log("inside updateUserProgress 1.1", name, emailId);
      //   const userProgressData = {
      //     userName: name,
      //     emailId: emailId,
      //     progress,
      //     totalScore,
      //     redeemedScores: redeemedScores,
      //     finishedTasks: finishedTasks,
      //   };
      //   // Update session storage

      //   // await httpService.post(axios.baseURL, userProgressData);
      // }
    } catch (error) {
      console.error("Error updating user progress:", error);
    }
  };

  // Load initial state from backend on component mount
  // useEffect(() => {
  //   console.log("inside userProgressContext useEffect 1", name, clientIP);
  //   try {
  //     if (name && clientIP) {
  //       console.log("inside userProgressContext useEffect 1.1");
  //       fetchUserProgressFromBackend();
  //     }
  //   } catch (error) {
  //     console.error("Error fetching client IP:", error);
  //   }
  // }, [name, clientIP]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update session storage whenever the state changes
  useEffect(() => {
    sessionStorage.setItem("isStartOver", JSON.stringify(isStartOver));
  }, [isStartOver]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.setItem(
      "isUserProgressFetched",
      JSON.stringify(isUserProgressFetched)
    );
  }, [isUserProgressFetched]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    updateUserProgress("progress", JSON.stringify(progress));
  }, [progress]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.setItem("localProgress", JSON.stringify(localProgress));
  }, [localProgress]);
  useEffect(() => {
    sessionStorage.setItem("isLevelEnd", JSON.stringify(isLevelEnd));
  }, [isLevelEnd]);
  useEffect(() => {
    updateUserProgress("name", name);
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.setItem("emailId", emailId);
  }, [emailId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    updateUserProgress("totalScore", totalScore);
  }, [totalScore]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    updateUserProgress(
      "redeemedScores",
      JSON.stringify(Array.from(redeemedScores))
    );
  }, [redeemedScores]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.setItem("currentTool", JSON.stringify(currentTool));
  }, [currentTool]);
  useEffect(() => {
    updateUserProgress(
      "finishedTasks",
      JSON.stringify(Array.from(finishedTasks))
    );
  }, [finishedTasks]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    updateUserProgress(
      "firstAttemptKeys",
      JSON.stringify(Array.from(firstAttemptKeys))
    );
  }, [firstAttemptKeys]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.setItem("completedMinigames", JSON.stringify(Array.from(completedMinigames)));
  }, [completedMinigames]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.setItem("gridNodeStates", JSON.stringify(gridNodeStates));
  }, [gridNodeStates]);
  useEffect(() => {
    if (difficulty) {
      sessionStorage.setItem("difficulty", difficulty);
      localStorage.setItem("lastDifficulty", difficulty);
    } else {
      sessionStorage.removeItem("difficulty");
    }
  }, [difficulty]);

  const addFirstAttemptKey = (key) => {
    setFirstAttemptKeys((prev) => new Set([...prev, key]));
  };

  const addCompletedMinigame = (key) => {
    setCompletedMinigames((prev) => new Set([...prev, key]));
  };

  const flipNode = useCallback((nodeId) => {
    if (!GRID_NODE_IDS.includes(nodeId)) return;
    setGridNodeStates((prev) => {
      const idx = GRID_STATE_CYCLE.indexOf(prev[nodeId] ?? 'green');
      return { ...prev, [nodeId]: GRID_STATE_CYCLE[(idx + 1) % GRID_STATE_CYCLE.length] };
    });
  }, []);

  const setNodeState = useCallback((nodeId, state) => {
    if (!GRID_NODE_IDS.includes(nodeId)) return;
    if (!GRID_STATE_CYCLE.includes(state)) return;
    setGridNodeStates((prev) => ({ ...prev, [nodeId]: state }));
  }, []);

  // Handle storage event
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.storageArea === sessionStorage) {
        if (event.key === "isStartOver") {
          setIsStartOver(JSON.parse(event.newValue));
        } else if (event.key === "isUserProgressFetched") {
          setIsUserProgressFetched(JSON.parse(event.newValue));
        } else if (event.key === "progress") {
          setProgress(JSON.parse(event.newValue));
        } else if (event.key === "localProgress") {
          setLocalProgress(event.newValue);
        } else if (event.key === "isLevelEnd") {
          setIsLevelEnd(event.newValue);
        } else if (event.key === "name") {
          setName(JSON.parse(event.newValue));
        } else if (event.key === "emailId") {
          setEmailId(JSON.parse(event.newValue));
        } else if (event.key === "totalScore") {
          setTotalScore(JSON.parse(event.newValue));
        } else if (event.key === "redeemedScores") {
          setRedeemedScores(new Set(JSON.parse(event.newValue)));
        } else if (event.key === "currentTool") {
          setCurrentTool(new Set(JSON.parse(event.newValue)));
        } else if (event.key === "finishedTasks") {
          setFinishedTasks(new Set(JSON.parse(event.newValue)));
        } else if (event.key === "firstAttemptKeys") {
          setFirstAttemptKeys(new Set(JSON.parse(event.newValue)));
        } else if (event.key === "completedMinigames") {
          setCompletedMinigames(new Set(JSON.parse(event.newValue)));
        } else if (event.key === "gridNodeStates") {
          setGridNodeStates(JSON.parse(event.newValue));
        } else if (event.key === "difficulty") {
          setDifficulty(event.newValue);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const lp = sessionStorage.getItem("localProgress");
      if (lp && parseFloat(lp) > 0) {
        e.preventDefault();
        e.returnValue = "You're in the middle of a level. Progress may be lost.";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return (
    <UserProgressContext.Provider
      value={{
        audioMuted,
        setAudioMuted,
        audioSrc,
        setAudioSrc,
        // clientIP,
        // setClientIP,
        isStartOver,
        setIsStartOver,
        isUserProgressFetched,
        setIsUserProgressFetched,
        progress,
        setProgress,
        localProgress,
        setLocalProgress,
        isLevelEnd,
        setIsLevelEnd,
        name,
        setName,
        emailId,
        setEmailId,
        totalScore,
        setTotalScore,
        redeemedScores,
        setRedeemedScores,
        currentTool,
        setCurrentTool,
        finishedTasks,
        setFinishedTasks,
        firstAttemptKeys,
        addFirstAttemptKey,
        completedMinigames,
        addCompletedMinigame,
        nextButtonLocked,
        setNextButtonLocked,
        gridNodeStates,
        setGridNodeStates,
        flipNode,
        setNodeState,
        difficulty,
        setDifficulty,
        sessionExpired,
        setSessionExpired,
      }}
    >
      {children}
      {sessionExpired && (
        <div className="fixed inset-0 z-[9999] flex items-end justify-center p-6 pointer-events-none">
          <div className="bg-red-900/95 border border-red-500 text-white px-6 py-4 rounded-lg shadow-2xl pointer-events-auto max-w-sm w-full">
            <p className="text-sm font-semibold mb-1">Session expired</p>
            <p className="text-xs text-red-200 mb-3">
              Your session has expired (progress: level {Math.trunc(parseFloat(progress || 0))}). Please log in again.
            </p>
            <a
              href={`${PUBLIC_URL}/`}
              className="inline-block text-xs bg-red-700 hover:bg-red-600 px-4 py-1.5 rounded font-semibold tracking-wide uppercase"
            >
              Log in again
            </a>
            <button
              onClick={() => setSessionExpired(false)}
              className="ml-3 text-xs text-red-300 hover:text-white"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </UserProgressContext.Provider>
  );
};

export default UserProgressContext;
